import RouteTemplate from 'ember-route-template';

import { get } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import LinksTo from 'ember-route-state/components/links-to.gjs';
import linkToState from 'ember-route-state/helpers/link-to-state.js';
import { pageTitle } from 'ember-page-title';

import CButton from 'rise-ui/components/c-button.gjs';
import ExpandingBlock from 'rise-ui/components/expanding-block.gjs';
import PageControls from 'rise-ui/components/page-controls.gjs';
import ReloadModel from 'rise-ui/components/reload-model.gjs';
import Select from 'rise-ui/components/select.gjs';
import Sticky from 'rise-ui/components/sticky.gjs';
import NextStep from 'rise-ui/components/time-series/next-step.gjs';
import SearchResult from 'rise-ui/components/time-series/search/result.gjs';
import SearchFilters from 'rise-ui/components/time-series/search/filters.gjs';
import SearchHelp from 'rise-ui/components/time-series/search/help.gjs';
import SearchSelected from 'rise-ui/components/time-series/search/selected.gjs';
import SearchTerm from 'rise-ui/components/time-series/search/term.gjs';
import classMod from 'rise-ui/helpers/class-mod.js';
import event from 'rise-ui/helpers/event.js';
import ever from 'rise-ui/helpers/ever.js';
import nor from 'rise-ui/helpers/nor.js';
import or from 'rise-ui/helpers/or.js';
import set from 'rise-ui/helpers/set.js';
import string from 'rise-ui/helpers/string.js';
import sticky from 'rise-ui/modifiers/sticky.js';
import { readOnly, or as orComputed } from 'rise-ui/utils/object/computed.js';
import { stripEmpties } from 'rise-ui/utils/query-params.js';

export default RouteTemplate(class TimeSeriesSearchTemplate extends Component {
  @service router;
  @service ssr;
  @service('time-series/params') timeSeriesParams;
  @service('time-series/selected-locations') timeSeriesLocations;
  @service('time-series/search') timeSeriesSearch;

  orderDefault = 'relevance';
  @orComputed('{@controller.order,orderDefault}') orderValue;

  @readOnly('timeSeriesParams.hasLocation') hasLocation;
  @readOnly('timeSeriesSearch.results') results;

  @tracked isStickyHelpShown = false;
  @tracked isHelpShown = false;

  @action doSort(value) {
    this.router.transitionTo(
      this.router.currentRouteName,
      { queryParams: stripEmpties({
          order: value || undefined,
        }) }
    );
  }

  <template>
    {{pageTitle "Select Locations to Query Time Series Data"}}

    {{#let (ever this.results.isResolved) as | resultsInit |}}
    <div class="c-section qa-search">

      <Sticky @name="help">
        <ExpandingBlock @button={{true}} @onToggle={{set this "isStickyHelpShown"}}>
          <:head>
            {{if this.isStickyHelpShown "Hide" "Show"}} Instructions
          </:head>
          <:body>
            <SearchHelp />
          </:body>
        </ExpandingBlock>
      </Sticky>

      <ExpandingBlock @button={{true}}
        @onToggle={{set this "isHelpShown"}}
        {{sticky activate="help"}}
      >
        <:head>
          {{if this.isHelpShown "Hide" "Show"}} Instructions
        </:head>
        <:body>
          <SearchHelp />
        </:body>
      </ExpandingBlock>

      <SearchSelected />

      <div class="c-search">
        <div class="c-search__order">
          Order by:
          <Select class="c-search__order-control"
            onchange={{event this.doSort}}
            @selected={{this.orderValue}} as | select |>
            <select.Option @value="relevance">Relevance</select.Option>
            <select.Option @value="newest">Last Location Update (Newest)</select.Option>
            <select.Option @value="oldest">Last Location Update (Oldest)</select.Option>
            <select.Option @value="title">Alphabetical by Location Name</select.Option>
          </Select>
        </div>

        <div class="c-search__query">
          <h4 class="c-section-title c-search__query-label">Search</h4>
          <div class="c-search__query-reset">
            <CButton @activeDisabled={{true}}
              @activeTitle="Search is Clear"
              @linkTo={{get this.timeSeriesSearch "linkToClear"}}
            >Clear Search/Filters</CButton>
          </div>

          <Sticky @name="search" @type="slim">
            <SearchTerm/>
          </Sticky>
          <div class="c-search__query-term"
            {{sticky activate="search"}}
          >
            <SearchTerm/>
          </div>

          <div class="c-search__query-hint">
            Search the catalog record title, description, tags, and generation effort
            and the item title and description, and the location name, description and tags.
            Operators are available to require inclusion (`+`), require exclusion (`-`),
            and require an exact phrase to appear in the search results (quotation marks).
            Boolean operators AND, OR, and NOT are also available.
            <LinksTo @route="help.page" @model="0e80bdc5-a4b9-4079-a52e-a20e0ddb9ff0">Details</LinksTo>
          </div>
        </div>

        <Sticky @name="filters--medium" @type="medium">
          <div class="c-search__filters">
            <SearchFilters />
          </div>
        </Sticky>
        <div class="c-search__filters"
          {{sticky activate="filters--medium"}}
        >
          <SearchFilters />
        </div>

        {{#if this.results.isPending}}
          <div class="c-search__meta c-search__meta--loading qa-search-loading">Loading Locations...</div>
        {{else if resultsInit}}
          <div class="c-search__meta c-search__meta--count">
            {{or this.results.value.meta.totalItems "0"}} locations found
          </div>
        {{/if}}

        <div class="c-search__results qa-search-results">
          <h4 class="c-section-title c-search__results-label">Results</h4>

          <div class="c-search__paging--before">
            <PageControls @config={{this.timeSeriesSearch.paging}} />
          </div>

          <div class={{classMod "c-search__results"
            resultsInit "--init"
            (nor resultsInit this.results.value.length) "--empty"
          }}>
            {{#if this.results.isRejected}}
              <div class="c-no-result c-search__results-error qa-search-error">
                Error fetching locations. <ReloadModel>Retry</ReloadModel>
              </div>
            {{else}}
              {{#each this.results.value as | location |~}}
                <SearchResult @location={{location}}
                  @added={{get this.hasLocation location.id}}
                  @disableAdd={{this.hasMaxLocations}}
                  @warningAdd={{if this.hasMaxLocations "Maximum locations selected"}}
                />
              {{~else}}
                {{#if (or this.results.isPending this.ssr.isSsr)}}
                  <div class="c-no-result c-search__results-init qa-search-loading">Loading Locations...</div>
                {{else}}
                  <div class="c-no-result c-search__results-empty qa-search-empty">No results match search and/or filter criteria</div>
                {{/if}}
              {{/each}}
            {{/if}}
          </div>

          <div class="c-search__paging--after">
            <PageControls @config={{this.timeSeriesSearch.paging}} />
          </div>
        </div>
      </div>

      <div class="c-section c-actions">
        <NextStep
          @linkTo={{linkToState "time-series.selection"}}
          @title="Continue to {{string "time-series-selection-route"}}"
        >
          Next Step: {{string "time-series-selection-route"}}
        </NextStep>
      </div>
    </div>
    {{/let}}
  </template>
});
